/**
 * CR-6347: Reworked PO Lines filter panel for the Customer View.
 *
 * Mirrors the supplier sectioned panel (CR-6348): filters are grouped into
 * labelled sections — Date Range / Company / Supplier / Classification / Order
 * Details — and the legacy ODD Status + OTD Status to LSPD filters are exposed.
 * Per the supplied diagram the filter header stays visible when the sections are
 * collapsed and carries the Refresh / Export / Hide-filters controls, while the
 * footer holds Clear All / Apply Filters.
 *
 * Existing behaviour is preserved: input types are unchanged, the organisation
 * hierarchy view-scoping (viewScopedCustomers) still narrows the Company Site
 * list, the Division -> Site -> Warehouse and Supplier Group -> Site cascades
 * carry over via the shared hook, and CR-4716 permission gating (Buyer ID / OTIF
 * Overridden / PPV) and the schedule feature-flag gating remain intact.
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../../ui/card';
import { Button } from '../../../../ui/button';
import { Search, X, Calendar, RefreshCw, Download, Filter, Building2, Truck, Tags, FileText } from 'lucide-react';
import {
  POLinesFilters,
  FilterOptions,
  ERP_ORDER_TYPE_OPTIONS,
} from '../types';
import { useAuth } from '../../../../../contexts/AuthContext';
import { useView } from '../../../../../contexts/ViewContext';
import { useMenuSettings } from '../../../../../contexts/MenuSettingsContext';
import { usePortalMode } from '../../../../../hooks/usePortalMode';
import { FIELD_CLS, LABEL_CLS, FilterSection } from './fieldStyles';
import { CompanySection } from './sections/CompanySection';
import { SupplierSection } from './sections/SupplierSection';
import { ClassificationSection } from './sections/ClassificationSection';
import { OrderDetailsSection } from './sections/OrderDetailsSection';
import { usePOLinesFilterCascades } from './usePOLinesFilterCascades';

/** Horizontal divider with padding so it doesn't touch card edges */
const SectionDivider = () => (
  <div className="mx-4 border-t border-border" />
);

interface CustomerSectionedFiltersProps {
  filters: POLinesFilters;
  filterOptions: FilterOptions | null;
  onFilterChange: (key: string, value: string) => void;
  onSearch: () => void;
  onClearFilters: () => void;
  // CR-6347: the header persists even when the sections are collapsed, so the
  // panel owns the show/hide toggle plus the Refresh + Export controls the
  // diagram moved out of the results toolbar.
  showFilters: boolean;
  onToggleFilters: () => void;
  onRefresh: () => void;
  onExport: (format: 'xlsx' | 'csv') => void;
  exporting: boolean;
}

// Search-helper keys excluded from the "active" count.
const NON_COUNTED_KEYS = new Set(['supplierSearch', 'customerSearch']);

export function CustomerSectionedFilters({
  filters,
  filterOptions,
  onFilterChange,
  onSearch,
  onClearFilters,
  showFilters,
  onToggleFilters,
  onRefresh,
  onExport,
  exporting,
}: CustomerSectionedFiltersProps) {
  // CR-4716: keep the commercial-filter permission gate identical to the flat
  // panel (Buyer ID / OTIF Overridden / PPV hidden from supplier users).
  const { user } = useAuth();
  const isSupplierUser = user?.platform === 'SUPPLIER';

  const { isFeatureEnabled } = useMenuSettings();
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration');
  const poLineSchedulesEnabled = isFeatureEnabled('po_line_schedules');

  // CR-4577: narrow the Company Site list to the active view context.
  const { viewLevel, selectedCompanyId, selectedGroupId } = useView();
  const { isSupplier } = usePortalMode();
  const viewScopedCustomers = useMemo(() => {
    const all = filterOptions?.customers || [];
    if (isSupplier) return all;
    if (viewLevel === 'SITE' && selectedCompanyId) return all.filter((c) => c.id === selectedCompanyId);
    if (viewLevel === 'COMPANY' && selectedCompanyId) return all.filter((c) => c.id === selectedCompanyId);
    if (viewLevel === 'GROUP' && selectedGroupId && selectedGroupId > 0) {
      const inGroup = all.filter((c) => c.group === selectedGroupId);
      return inGroup.length > 0 ? inGroup : all;
    }
    return all;
  }, [filterOptions?.customers, viewLevel, selectedCompanyId, selectedGroupId, isSupplier]);

  const divisions = filterOptions?.divisions || [];
  const supplierGroups = filterOptions?.supplier_groups || [];
  const siteCountries = filterOptions?.site_countries || [];
  const supplierCountries = filterOptions?.supplier_countries || [];
  const commodityGroups = filterOptions?.commodity_groups || [];
  const commodityTypes = filterOptions?.commodity_types || [];

  // Shared dependent-filter cascades, seeded with the view-scoped company list.
  const {
    companySiteOptions,
    warehouseOptions,
    supplierSiteOptions,
    handleCompanyCountryChange,
    handleDivisionChange,
    handleCompanySiteChange,
    handleSupplierGroupChange,
    handleSupplierCountryChange,
  } = usePOLinesFilterCascades(filters, filterOptions, onFilterChange, viewScopedCustomers);

  const handleCommodityGroupChange = (value: string) => {
    onFilterChange('commodityGroup', value);
    if (value !== filters.commodityGroup) {
      onFilterChange('commodityType', '');
    }
  };

  const activeCount = useMemo(
    () =>
      Object.entries(filters).filter(
        ([k, v]) => !NON_COUNTED_KEYS.has(k) && v !== '' && v != null,
      ).length,
    [filters],
  );

  // ERP Order Type + On Hold live in Order Details per the diagram but are
  // feature-flag gated, so they are injected rather than baked into the shared
  // section component.
  const orderDetailExtras = (
    <>
      {poLineSchedulesEnabled && (
        <div>
          <label className={LABEL_CLS}>ERP Order Type</label>
          <select
            value={filters.erpOrderType}
            onChange={(e) => onFilterChange('erpOrderType', e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">All Types</option>
            {ERP_ORDER_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}
      {!scheduleCollabEnabled && (
        <div>
          <label className={LABEL_CLS}>On Hold</label>
          <select
            value={filters.on_hold}
            onChange={(e) => onFilterChange('on_hold', e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">All</option>
            <option value="yes">On Hold</option>
            <option value="no">Not On Hold</option>
          </select>
        </div>
      )}
    </>
  );

  return (
    <Card>
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl">Filter PO Lines</CardTitle>
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onExport('xlsx')}
              disabled={exporting}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <button
              type="button"
              onClick={onToggleFilters}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                showFilters
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  showFilters
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}>
                  {activeCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>

      {showFilters && (
        <CardContent className="p-0">
          <form onSubmit={(e) => { e.preventDefault(); onSearch(); }} className="space-y-0">
            {/* CR-6347: Sections with shaded headers, icons and dividers matching Pricebooks layout */}
            <div className="[&>section]:border-t-0">
              <SectionDivider />

              {/* Date Range */}
              <FilterSection title="Date Range" icon={Calendar}>
                <div>
                  <label className={LABEL_CLS}>ODD From</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="date"
                      value={filters.oddFrom}
                      onChange={(e) => onFilterChange('oddFrom', e.target.value)}
                      className={`${FIELD_CLS} pl-9`}
                    />
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLS}>ODD To</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="date"
                      value={filters.oddTo}
                      onChange={(e) => onFilterChange('oddTo', e.target.value)}
                      className={`${FIELD_CLS} pl-9`}
                    />
                  </div>
                </div>
              </FilterSection>

              <SectionDivider />

              <CompanySection
                filters={filters}
                onFilterChange={onFilterChange}
                siteCountries={siteCountries}
                divisions={divisions}
                companySiteOptions={companySiteOptions}
                warehouseOptions={warehouseOptions}
                onCompanyCountryChange={handleCompanyCountryChange}
                onDivisionChange={handleDivisionChange}
                onCompanySiteChange={handleCompanySiteChange}
                icon={Building2}
              />

              <SectionDivider />

              <SupplierSection
                filters={filters}
                onFilterChange={onFilterChange}
                supplierCountries={supplierCountries}
                supplierGroups={supplierGroups}
                supplierSiteOptions={supplierSiteOptions}
                onSupplierGroupChange={handleSupplierGroupChange}
                onSupplierCountryChange={handleSupplierCountryChange}
                icon={Truck}
              />

              <SectionDivider />

              <ClassificationSection
                filters={filters}
                onFilterChange={onFilterChange}
                commodityGroups={commodityGroups}
                commodityTypes={commodityTypes}
                onCommodityGroupChange={handleCommodityGroupChange}
                icon={Tags}
              />

              <SectionDivider />

              <OrderDetailsSection
                filters={filters}
                onFilterChange={onFilterChange}
                isSupplierUser={isSupplierUser}
                showPartAndDescription={false}
                extraFields={orderDetailExtras}
                icon={FileText}
              />
            </div>

            {/* Footer actions - CR-6347: Clear All left, Apply Filters right */}
            <div className="flex items-center justify-between px-6 py-4">
              <Button type="button" variant="outline" onClick={onClearFilters} className="px-6">
                <X className="h-4 w-4 mr-2" />
                Clear All
              </Button>
              <Button type="submit" className="px-6">
                <Search className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </form>
        </CardContent>
      )}
    </Card>
  );
}
